// ── FriendRadar — Friend-defined axis radar chart ───────────────────────────
// Used in Profile (own view) and Discover/Matches (comparison view).
//
// Props:
//   selfScores   {axis: 0-10}  — how MY friends describe ME        (gold)
//   needScores   {axis: 0-10}  — what MY friends say I NEED        (gold, dashed) — only in comparison
//   theirScores  {axis: 0-10}  — how THEIR friends describe THEM   (red/teal)
//   theirNeeds   {axis: 0-10}  — what THEIR friends say THEY NEED  (red/teal, dashed)
//   mode: 'own' | 'radar1' | 'radar2'
//     own    → single polygon: selfScores (gold). No red.
//     radar1 → selfScores (gold solid) + theirNeeds (red solid) — "Am I what they need?"
//     radar2 → needScores (gold solid) + theirScores (red solid) — "Are they what I need?"
//   size: number (default 260)
//   isDark: bool

import { useApp } from '../contexts/AppContext';

const AXES = [
  { key:'repair',         en:'Repair',         fr:'Réparation'       },
  { key:'conflict',       en:'Conflict',        fr:'Conflit'          },
  { key:'accountability', en:'Accountability',  fr:'Responsabilité'   },
  { key:'generosity',     en:'Generosity',      fr:'Générosité'       },
  { key:'presence',       en:'Presence',        fr:'Présence'         },
  { key:'narrative',      en:'Self-aware',      fr:'Conscience de soi'},
  { key:'stability',      en:'Stability',       fr:'Stabilité'        },
  { key:'boredom',        en:'Adventure',       fr:'Aventure'         },
  { key:'social',         en:'Social',          fr:'Social'           },
  { key:'stakes',         en:'Ambition',        fr:'Ambition'         },
];

export default function FriendRadar({
  selfScores = {}, needScores = {}, theirScores = {}, theirNeeds = {},
  mode = 'own', size = 260, isDark = null,
  myName = 'You', theirName = 'Them',
}) {
  const { lang, theme } = useApp();
  const dark = isDark !== null ? isDark : theme !== 'light';
  const n   = AXES.length;
  const cx  = size / 2;
  const cy  = size / 2;
  const r   = size * 0.31;
  const lr  = size * 0.46;
  const fs  = Math.max(8, size * 0.058);

  // Colors
  const gold     = dark ? '#C9A84C' : '#9B6E10';
  const red      = dark ? '#E2785A' : '#C0453A';
  const gridC    = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const outerC   = dark ? 'rgba(201,168,76,0.25)'  : 'rgba(155,110,16,0.25)';
  const spokeC   = dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const labelC   = dark ? 'rgba(250,250,248,0.55)' : 'rgba(30,24,18,0.85)';
  const ringLblC = dark ? 'rgba(250,250,248,0.3)'  : 'rgba(30,24,18,0.35)';

  const point = (i, radius) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  };

  const toPath = (pts) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z';

  const makePts = (scores) =>
    AXES.map((ax, i) => point(i, r * Math.min(1, Math.max(0, (scores?.[ax.key] || 0) / 10))));

  // Determine what polygons to draw
  const goldPts = mode === 'radar2' ? makePts(needScores)  : makePts(selfScores);
  const redPts  = mode === 'own'    ? makePts(needScores)
                : mode === 'radar2' ? makePts(theirScores) : makePts(theirNeeds);
  const showRed = mode !== 'own' || Object.keys(needScores || {}).length > 0;

  const rings = [0.25, 0.5, 0.75, 1.0];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:'visible', display:'block' }}>
      <defs>
        <filter id={`glow-fr-${mode}`}>
          <feGaussianBlur stdDeviation="2" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Grid rings */}
      {rings.map((pct, ri) => (
        <polygon key={ri}
          points={AXES.map((_, i) => { const p = point(i, r * pct); return `${p.x},${p.y}`; }).join(' ')}
          fill="none"
          stroke={pct === 1.0 ? outerC : gridC}
          strokeWidth={pct === 1.0 ? 1 : 0.5}
        />
      ))}

      {/* Ring value labels */}
      {[2,4,6,8,10].map(val => {
        const p = point(1, r * (val / 10));
        return (
          <text key={val} x={p.x + 3} y={p.y + 3}
            fontSize={fs * 0.6} fill={ringLblC}
            fontFamily="'Courier New',monospace" textAnchor="start">{val}</text>
        );
      })}

      {/* Spokes */}
      {AXES.map((_, i) => {
        const o = point(i, r);
        return <line key={i} x1={cx} y1={cy} x2={o.x} y2={o.y} stroke={spokeC} strokeWidth={0.5}/>;
      })}

      {/* Difference area — XOR of both polygons (non-overlapping zones); distinct teal to contrast gold+red */}
      {showRed && (
        <path
          d={toPath(goldPts) + ' ' + toPath(redPts)}
          fill="rgba(68,200,230,0.38)"
          fillRule="evenodd"
          stroke="none"
          opacity={0.9}
        />
      )}

      {/* Red polygon (comparison — "them") */}
      {showRed && (
        <path d={toPath(redPts)}
          fill={`${red}10`} stroke={red} strokeWidth={1.6} opacity={0.85}/>
      )}
      {showRed && redPts.map((p, i) => (
        <circle key={`r${i}`} cx={p.x} cy={p.y} r={2} fill={red} opacity={0.85}/>
      ))}

      {/* Gold polygon ("me") */}
      <path d={toPath(goldPts)}
        fill={`${gold}10`} stroke={gold} strokeWidth={1.8}
        filter={`url(#glow-fr-${mode})`} opacity={0.95}/>
      {goldPts.map((p, i) => (
        <circle key={`g${i}`} cx={p.x} cy={p.y} r={2.2} fill={gold} opacity={0.95}/>
      ))}

      {/* Axis labels */}
      {AXES.map((ax, i) => {
        const lp    = point(i, lr);
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const anchor = Math.abs(Math.cos(angle)) < 0.15 ? 'middle'
                     : Math.cos(angle) > 0 ? 'start' : 'end';
        const dy = Math.sin(angle) < -0.5 ? -4 : Math.sin(angle) > 0.5 ? fs + 2 : fs * 0.35;
        const gv = (mode === 'radar2' ? needScores   : selfScores)?.[ax.key]  || 0;
        const rv = (mode === 'radar2' ? theirScores  : theirNeeds)?.[ax.key]  || 0;
        const highlight = showRed && gv >= 7 && rv >= 7;
        return (
          <text key={i} x={lp.x} y={lp.y + dy}
            fontSize={fs} fill={highlight ? gold : labelC}
            fontFamily="'Outfit','Helvetica',sans-serif"
            fontWeight={highlight ? '600' : '400'}
            textAnchor={anchor}>
            {lang === 'fr' ? ax.fr : ax.en}
          </text>
        );
      })}
    </svg>
  );
}

// ── Legend row ─────────────────────────────────────────────────────────────
export function FriendRadarLegend({ mode, myName = 'You', theirName = 'Them', isDark = null, showNeeds = false }) {
  const { lang, theme } = useApp();
  const dark = isDark !== null ? isDark : theme !== 'light';
  const fr = lang === 'fr';
  const gold = dark ? '#C9A84C' : '#9B6E10';
  const red  = dark ? '#E2785A' : '#C0453A';
  const sub  = dark ? 'rgba(250,250,248,0.45)' : 'rgba(30,24,18,0.5)';

  const entries = mode === 'own'
    ? showNeeds
      ? [{ color: gold, label: fr ? 'Comment tes amis te voient' : 'How friends see you' },
         { color: red,  label: fr ? 'Ce dont tu as besoin (selon tes amis)' : 'What friends say you need' }]
      : [{ color: gold, label: fr ? 'Comment tes amis te voient' : 'How your friends see you' }]
    : mode === 'radar1'
    ? [{ color: gold, label: fr ? `Comment les amis de ${myName} voient ${myName}` : `How ${myName}'s friends see ${myName}` },
       { color: red,  label: fr ? `Ce dont ${theirName} a besoin (selon ses amis)` : `What ${theirName}'s friends say ${theirName} needs` }]
    : [{ color: gold, label: fr ? `Ce dont ${myName} a besoin (selon ses amis)` : `What ${myName}'s friends say ${myName} needs` },
       { color: red,  label: fr ? `Comment les amis de ${theirName} voient ${theirName}` : `How ${theirName}'s friends see ${theirName}` }];

  return (
    <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', justifyContent:'center', marginTop:'0.5rem' }}>
      {entries.map((e, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.62rem', color: sub }}>
          <div style={{ width:16, height:2, background:e.color, borderRadius:1 }}/>
          {e.label}
        </div>
      ))}
    </div>
  );
}
